import type { Command, CommandInput } from "@vortex-os/slash-commands";
import { createHandoffSkeleton } from "../handoff.js";

export interface HandoffCreateResult {
  readonly path: string;
  readonly date: string;
  readonly time: string;
}

/**
 * `/handoff [title]` — Create a new, empty session hand-off file under
 * `data/_handoff/` (named `YYYY-MM-DD_HHMM.md`, with a `-2` suffix only on a
 * same-minute collision) and return its path for the agent to fill in.
 *
 * Deliberately small, mirroring `/log`: the host creates the file SAFELY
 * (atomic exclusive create, so a concurrent session never clobbers another's
 * baton), and the agent writes the actual hand-off content into the returned
 * file. The hand-off is a forward-looking baton kept separate from the worklog;
 * old ones are swept to `_handoff/_archive/` automatically at session start.
 */
export const handoffCommand: Command<HandoffCreateResult> = {
  name: "handoff",
  description:
    "Create a new session hand-off file under data/_handoff/ and return its path to fill in. The next session resumes from it; old hand-offs are auto-archived.",
  args: [
    {
      name: "title",
      description: "Optional short title for the hand-off (rest of the input).",
      required: false,
    },
  ],
  handler: async (input: CommandInput): Promise<HandoffCreateResult> => {
    const title = input.rest.trim();
    const res = await createHandoffSkeleton(input.context.dataDir, {
      now: new Date(),
      ...(title ? { title } : {}),
    });
    return { path: res.path, date: res.date, time: res.time };
  },
};
